import { useSavedWork } from './useSavedWork';
import type { Operation } from './outbox';

/**
 * Projected stock (.docs/023): what this PC's own unsent work will take out
 * of a store, so a line picker can say what will really be left — and most
 * refusals for "not enough stock" show before anything is sent, not hours
 * later.
 *
 * Only work that will be **posted** counts: a draft moves no stock. Work
 * already sent keeps counting until the store's figures have been read again
 * after it went, or the figure would jump back up in between.
 */

interface Line {
  skuId: string;
  qty: string;
  batchId?: string;
  storeId?: string;
}

/**
 * How much this PC's unsent work takes out of `storeId` for an item — or for
 * one lot of it — net of what it brings in. `since`: when the store's figure
 * was read; work sent before then is in that figure already.
 */
export function queuedOut(
  ops: readonly Operation[],
  storeId: string,
  skuId: string,
  batchId: string | undefined,
  since: number,
): number {
  const posted = new Set(
    ops.filter((op) => op.kind.endsWith('.post') && op.dependsOn && op.state !== 'refused').map((op) => op.dependsOn!),
  );
  const counts = (op: Operation) =>
    op.state !== 'refused' && posted.has(op.id) && (op.state !== 'sent' || (op.sentAt ?? 0) > since);
  const sameLot = (line: { batchId?: string | undefined }) => !batchId || line.batchId === batchId;

  let total = 0;
  for (const op of ops) {
    if (!counts(op)) continue;
    const body = (op.body ?? {}) as {
      storeId?: string;
      fromStoreId?: string;
      toStoreId?: string;
      skuId?: string;
      qty?: string;
      batchId?: string;
      lines?: Line[];
    };
    if (op.kind === 'consumption.create' && body.storeId === storeId && body.skuId === skuId && sameLot(body)) {
      total += Number(body.qty) || 0;
    } else if (op.kind === 'transfer.create') {
      for (const line of body.lines ?? []) {
        if (line.skuId !== skuId) continue;
        if (body.fromStoreId === storeId && sameLot(line)) total += Number(line.qty) || 0;
        // What arrives is counted for the item, not a lot: its lot there is the server's to say.
        if (body.toStoreId === storeId && !batchId) total -= Number(line.qty) || 0;
      }
    } else if (op.kind === 'inward.create' && !batchId) {
      for (const line of body.lines ?? []) {
        if (line.storeId === storeId && line.skuId === skuId) total -= Number(line.qty) || 0;
      }
    }
  }
  return total;
}

/** `queuedOut` over the work saved on this PC, kept in step as it changes. */
export function useQueuedOut(): (storeId: string, skuId: string, batchId: string | undefined, since: number) => number {
  const { data } = useSavedWork();
  const ops = data ?? [];
  return (storeId, skuId, batchId, since) => queuedOut(ops, storeId, skuId, batchId, since);
}
